import { CheckIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import Skeleton from 'react-loading-skeleton';

import { formatCurrency } from '../../../../../common-utils/formatters';
import { parseAssureurs, parseGarants } from '../../../../utils/companyDetails';
import type { CompanyInfoBlockProps } from './types';

const ServiceMark = ({ active }: { active: boolean | undefined }) => (active
  ? <CheckIcon className="ml-1 inline h-6 w-6 text-rgpd-green" role="img" aria-label="Oui" />
  : <XMarkIcon className="ml-1 inline h-6 w-6 text-red-500" role="img" aria-label="Non" />);

/** Legal information of the CCI file (with Pappers when available): services, guarantees, card, officers. */
export default function CompanyLegalInfoBlock({ details, loading }: CompanyInfoBlockProps) {
  return (
    <div className="mb-4 rounded-lg bg-gray-50 p-4">
      <h3 className="text-md mb-2 font-semibold">Informations légales <span className="rounded-full bg-rgpd-blue px-2 py-1 font-serif text-sm whitespace-nowrap text-rgpd-white">CCI x Pappers</span></h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {loading ? (
          <>
            <div>
              <h4 className="mb-2 font-medium">Services</h4>
              <div className="space-y-1">
                <Skeleton width={200} height={24} />
                <Skeleton width={200} height={24} />
                <Skeleton width={200} height={24} />
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Garanties</h4>
              <div className="space-y-1">
                <Skeleton width={250} height={24} />
                <Skeleton width={200} height={24} />
                <Skeleton width={220} height={24} />
                <Skeleton width={240} height={24} />
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Carte professionnelle</h4>
              <div className="space-y-1">
                <Skeleton width={180} height={24} />
                <Skeleton width={200} height={24} />
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Dirigeant(s)</h4>
              <Skeleton width={220} height={24} />
            </div>
          </>
        ) : (
          <>
            <div>
              <h4 className="mb-2 font-medium">Services</h4>
              <div className="space-y-1">
                <p>Syndic :<ServiceMark active={details?.syndicPappers} /></p>
                <p>Gestion Immobilière :<ServiceMark active={details?.gestionImmobilierePappers} /></p>
                <p>Transaction :<ServiceMark active={details?.transactionPappers} /></p>
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Garanties</h4>
              <div className="space-y-1">
                {details?.garants && parseGarants(details.garants).map((garant, index) => (
                  <p key={index}>
                    {garant.nom} : {formatCurrency(parseFloat(garant.montant_de_la_garantie))}
                  </p>
                ))}
                {details?.assureurs && parseAssureurs(details.assureurs).map((assureur, index) => (
                  <p key={index}>Assureur RC Pro : {assureur.assureur_nom}</p>
                ))}
                {details?.banqueCompteSequestre && (
                  <p>Compte séquestre : {details.banqueCompteSequestre}</p>
                )}
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Carte professionnelle</h4>
              <div className="space-y-1">
                {details?.carte && (
                  <a
                    href={`https://www.cci.fr/agent-immobilier?numero_carte=${details.carte}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-rgpd-blue hover:text-rgpd-blue-dark"
                  >
                    <GlobeAltIcon className="mr-1 h-4 w-4" />
                    Ouvrir sur la CCI
                  </a>
                )}
                <p className="flex items-center">
                  Numéro : {details?.carte ? details.carte : 'N/A'}
                </p>
                <p>Date de fin : {details?.dateFinCarte ? new Date(details.dateFinCarte).toLocaleDateString('fr-FR') : 'N/A'}</p>
                {details?.cciLibelle && <p>Délivrée par : {details.cciLibelle}</p>}
              </div>
            </div>
            <div>
              <h4 className="mb-2 font-medium">Dirigeant(s)</h4>
              <p>{details?.infoDirigeant ?? 'N/A'}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
